import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './CookieBanner.scss';

const defaultConsent = {
    necessary: true,
    googleMaps: false,
    instagram: false,
    tracking: false,
    marketing: false
};

const CookieBanner = () => {
    const { t, i18n } = useTranslation();

    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settings, setSettings] = useState(defaultConsent);

    const [currentLanguage, setLanguage] = useState(i18n.language);

    useEffect(() => {
        const handleLanguageChange = () => {
            setLanguage(i18n.language);
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n]);


    useEffect(() => {
        const checkConsent = () => {
            const savedConsent =
                localStorage.getItem('cookieConsent');

            if (!savedConsent) {
                setShowBanner(true);
                return;
            }

            try {
                const parsedConsent = JSON.parse(savedConsent);

                setSettings({
                    ...defaultConsent,
                    ...parsedConsent,
                    necessary: true
                });

                setShowBanner(false);
            } catch (error) {
                console.error(
                    'Cookie consent error:',
                    error
                );

                localStorage.removeItem('cookieConsent');
                setShowBanner(true);
            }
        };

        checkConsent();
    }, []);

    const saveConsent = (consent) => {
        const finalConsent = {
            ...defaultConsent,
            ...consent,
            necessary: true
        };

        localStorage.setItem(
            'cookieConsent',
            JSON.stringify(finalConsent)
        );

        setSettings(finalConsent);

        window.dispatchEvent(
            new Event('cookieConsentChanged')
        );
    };

    const acceptAll = () => {
        saveConsent({
            googleMaps: true,
            instagram: true,
            tracking: true,
            marketing: true
        });

        setShowBanner(false);
        setShowSettings(false);
    };

    const rejectAll = () => {
        saveConsent({
            googleMaps: false,
            instagram: false,
            tracking: false,
            marketing: false
        });

        setShowBanner(false);
        setShowSettings(false);
    };

    const handleSettingChange = (category) => {
        setSettings((previous) => ({
            ...previous,
            [category]: !previous[category]
        }));
    };

    const saveSettings = () => {
        saveConsent(settings);

        setShowBanner(false);
        setShowSettings(false);
    };

    if (!showBanner) {
        return null;
    }

    return (
        <div
            className="cookie-overlay"
            key={currentLanguage}
        >
            <div className="cookie-banner">
                <div className="cookie-banner__content">

                    <h2>
                        {t('cookies.title')}
                    </h2>

                    {!showSettings ? (
                        <>
                            <p>
                                {t('cookies.description')}
                            </p>

                            <p>
                                {t('cookies.moreInfo')}{' '}

                                <Link to="/datenschutz">
                                    {t('cookies.privacy')}
                                </Link>
                                .
                            </p>

                            <div className="cookie-banner__buttons">

                                <button
                                    type="button"
                                    className="cookie-button cookie-button--secondary"
                                    onClick={rejectAll}
                                >
                                    {t(
                                        'cookies.buttons.necessary'
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className="cookie-button cookie-button--settings"
                                    onClick={() =>
                                        setShowSettings(true)
                                    }
                                >
                                    {t(
                                        'cookies.buttons.settings'
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className="cookie-button cookie-button--primary"
                                    onClick={acceptAll}
                                >
                                    {t(
                                        'cookies.buttons.acceptAll'
                                    )}
                                </button>

                            </div>
                        </>
                    ) : (
                        <div className="cookie-settings">

                            {/* NECESSARY COOKIES */}

                            <div className="cookie-settings__item">
                                <div>
                                    <h3>
                                        {t(
                                            'cookies.settings.necessary.title'
                                        )}
                                    </h3>

                                    <p>
                                        {t(
                                            'cookies.settings.necessary.description'
                                        )}
                                    </p>
                                </div>

                                <span className="cookie-settings__required">
                                    {t(
                                        'cookies.settings.alwaysActive'
                                    )}
                                </span>
                            </div>

                            {/* GOOGLE MAPS */}

                            <div className="cookie-settings__item">
                                <div>
                                    <h3>
                                        {t(
                                            'cookies.settings.googleMaps.title'
                                        )}
                                    </h3>

                                    <p>
                                        {t(
                                            'cookies.settings.googleMaps.description'
                                        )}
                                    </p>
                                </div>

                                <label className="cookie-switch">
                                    <input
                                        type="checkbox"
                                        checked={
                                            settings.googleMaps
                                        }
                                        onChange={() =>
                                            handleSettingChange(
                                                'googleMaps'
                                            )
                                        }
                                    />

                                    <span className="cookie-switch__slider"></span>
                                </label>
                            </div>

                            {/* INSTAGRAM */}

                            <div className="cookie-settings__item">
                                <div>
                                    <h3>
                                        {t(
                                            'cookies.settings.instagram.title'
                                        )}
                                    </h3>

                                    <p>
                                        {t(
                                            'cookies.settings.instagram.description'
                                        )}
                                    </p>
                                </div>

                                <label className="cookie-switch">
                                    <input
                                        type="checkbox"
                                        checked={
                                            settings.instagram
                                        }
                                        onChange={() =>
                                            handleSettingChange(
                                                'instagram'
                                            )
                                        }
                                    />

                                    <span className="cookie-switch__slider"></span>
                                </label>
                            </div>

                            {/* TRACKING */}

                            <div className="cookie-settings__item">
                                <div>
                                    <h3>
                                        {t(
                                            'cookies.settings.tracking.title'
                                        )}
                                    </h3>

                                    <p>
                                        {t(
                                            'cookies.settings.tracking.description'
                                        )}
                                    </p>
                                </div>

                                <label className="cookie-switch">
                                    <input
                                        type="checkbox"
                                        checked={
                                            settings.tracking
                                        }
                                        onChange={() =>
                                            handleSettingChange(
                                                'tracking'
                                            )
                                        }
                                    />

                                    <span className="cookie-switch__slider"></span>
                                </label>
                            </div>

                            {/* MARKETING */}

                            <div className="cookie-settings__item">
                                <div>
                                    <h3>
                                        {t(
                                            'cookies.settings.marketing.title'
                                        )}
                                    </h3>

                                    <p>
                                        {t(
                                            'cookies.settings.marketing.description'
                                        )}
                                    </p>
                                </div>

                                <label className="cookie-switch">
                                    <input
                                        type="checkbox"
                                        checked={
                                            settings.marketing
                                        }
                                        onChange={() =>
                                            handleSettingChange(
                                                'marketing'
                                            )
                                        }
                                    />

                                    <span className="cookie-switch__slider"></span>
                                </label>
                            </div>

                            {/* BUTTONS */}

                            <div className="cookie-banner__buttons">

                                <button
                                    type="button"
                                    className="cookie-button cookie-button--secondary"
                                    onClick={rejectAll}
                                >
                                    {t(
                                        'cookies.buttons.necessary'
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className="cookie-button cookie-button--primary"
                                    onClick={saveSettings}
                                >
                                    {t(
                                        'cookies.buttons.save'
                                    )}
                                </button>

                            </div>

                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default CookieBanner;
